import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useGetTestimonialRequestByIdQuery,
  useApproveTestimonialRequestMutation,
  useSetUseCaseAutoReminderMutation,
  useSendUseCaseReminderNowMutation,
} from '../../store/vendorApi';
import { ApproveTestimonialRequestRequest } from '@clareefai/types';
import { toast } from '@clareefai/ui';
import { requestStatusDisplay } from '../../utils/testimonialRequestStatus';
import { ArrowLeft, Check, X, Clock, Mail, User, Building, FileText, Loader, AlertCircle, Bell, RefreshCw } from 'lucide-react';

const TestimonialRequestDetail: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  const { data: request, isLoading, error } = useGetTestimonialRequestByIdQuery(id || '', { skip: !id });
  const [approveRequest, { isLoading: isSubmitting }] = useApproveTestimonialRequestMutation();
  const [toggleAutoReminder, { isLoading: isTogglingReminder }] = useSetUseCaseAutoReminderMutation();
  const [sendReminderNow, { isLoading: isRemindingNow }] = useSendUseCaseReminderNowMutation();

  const handleToggleAutoReminder = async (next: boolean) => {
    if (!id) return;
    try {
      await toggleAutoReminder({ id, enabled: next }).unwrap();
      toast.success(next ? t('useCases.request.toasts.autoRemindersOn') : t('useCases.request.toasts.autoRemindersOff'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('useCases.request.toasts.autoRemindersUpdateFailed'));
    }
  };

  const handleSendReminderNow = async () => {
    if (!id) return;
    try {
      await sendReminderNow(id).unwrap();
      toast.success(t('useCases.request.toasts.reminderSent'));
    } catch (err: any) {
      toast.error(err?.data?.message || t('useCases.request.toasts.reminderSendFailed'));
    }
  };

  const handleApprove = async () => {
    if (!id) return;

    try {
      const approveReq: ApproveTestimonialRequestRequest = {
        approved: true
      };
      await approveRequest({ id, request: approveReq }).unwrap();
      navigate('/use-cases');
    } catch (err: any) {
      console.error('Failed to approve request:', err);
    }
  };

  const handleReject = async () => {
    if (!id || !rejectionReason.trim()) return;

    try {
      const rejectReq: ApproveTestimonialRequestRequest = {
        approved: false,
        rejectionReason: rejectionReason.trim()
      };
      await approveRequest({ id, request: rejectReq }).unwrap();
      navigate('/use-cases');
    } catch (err: any) {
      console.error('Failed to reject request:', err);
    }
  };

  const getStatusBadge = (status: string, rejectedByRole?: string | null) => {
    const { badgeClass, labelKey } = requestStatusDisplay(status, rejectedByRole);
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badgeClass}`}>
        {t(labelKey)}
      </span>
    );
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      USE_CASE: t('testimonials.requestDetail.types.useCase'),
      REVIEW: t('testimonials.requestDetail.types.review'),
      VIDEO: t('testimonials.requestDetail.types.video'),
      ONE_ON_ONE: t('testimonials.requestDetail.types.oneOnOne')
    };
    return labels[type] || type;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">{t('testimonials.requestDetail.notFound')}</p>
          <button
            onClick={() => navigate('/use-cases')}
            className="mt-4 text-purple-600 hover:text-purple-800"
          >
            {t('testimonials.requestDetail.backToUseCases')}
          </button>
        </div>
      </div>
    );
  }

  const canApprove = request.status === 'PROMOTER_APPROVED';
  const canReject = request.status === 'PROMOTER_APPROVED';

  // Reminders only run while the promoter still has to act on the request.
  const isPending = request.status === 'VENDOR_REQUESTED';
  const autoReminderOn = request.autoReminderEnabled ?? true;
  const reminderCount = request.reminderCount ?? 0;
  const lastReminder = request.lastReminderSentAt ?? null;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm">
          <div className="border-b border-gray-200 p-6">
            <button
              onClick={() => navigate('/use-cases')}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('testimonials.requestDetail.backToUseCases')}
            </button>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('testimonials.requestDetail.heading')}</h1>
                <p className="text-gray-600 mt-1">{t('testimonials.requestDetail.requestTypeSuffix', { type: getTypeLabel(request.type) })}</p>
              </div>
              {getStatusBadge(request.status, request.rejectedByRole)}
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Promoter Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <User className="w-5 h-5 mr-2 text-purple-600" />
                {t('testimonials.requestDetail.promoterInformation')}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-sm text-gray-600">{t('testimonials.requestDetail.nameLabel')}</span>
                  <p className="font-medium text-gray-900">{request.promoterName}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">{t('testimonials.requestDetail.companyLabel')}</span>
                  <p className="font-medium text-gray-900">{request.promoterCompany || t('testimonials.requestDetail.naShort')}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-600">{t('testimonials.requestDetail.emailLabel')}</span>
                  <p className="font-medium text-gray-900">{request.promoterEmail}</p>
                </div>
                {request.productId && (
                  <div>
                    <span className="text-sm text-gray-600">{t('testimonials.requestDetail.productLabel')}</span>
                    <p className="font-medium text-gray-900">{request.productId}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <Clock className="w-5 h-5 mr-2 text-purple-600" />
                {t('testimonials.requestDetail.timeline')}
              </h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t('testimonials.requestDetail.requestSent')}</p>
                    <p className="text-xs text-gray-600">{new Date(request.requestedAt).toLocaleString()}</p>
                  </div>
                </div>
                {request.promoterApprovedAt && (
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{t('testimonials.requestDetail.promoterApproved')}</p>
                      <p className="text-xs text-gray-600">{new Date(request.promoterApprovedAt).toLocaleString()}</p>
                    </div>
                  </div>
                )}
                {request.vendorApprovedAt && (
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{t('testimonials.requestDetail.vendorApproved')}</p>
                      <p className="text-xs text-gray-600">{new Date(request.vendorApprovedAt).toLocaleString()}</p>
                    </div>
                  </div>
                )}
                {request.rejectedAt && (
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{t('testimonials.requestDetail.rejected')}</p>
                      <p className="text-xs text-gray-600">{new Date(request.rejectedAt).toLocaleString()}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Content Preview (for USE_CASE) */}
            {request.type === 'USE_CASE' && request.content && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-purple-600" />
                  {t('testimonials.requestDetail.useCaseContent')}
                </h3>
                <div className="bg-white border border-gray-200 rounded p-4 max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
                    {request.content}
                  </pre>
                </div>
              </div>
            )}

            {/* Rejection Reason */}
            {request.rejectionReason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-900 mb-2">{t('testimonials.requestDetail.rejectionReason')}</h3>
                <p className="text-sm text-red-800">{request.rejectionReason}</p>
              </div>
            )}

            {/* Auto-reminder (DEV-92) — USE_CASE only. The scheduler nudges the
                promoter weekly (max 3) until they fill the use case; completion
                flips the toggle off. Manual nudges and the toggle only apply
                while the request is still awaiting the promoter. */}
            {request.type === 'USE_CASE' && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <Bell className="w-5 h-5 mr-2 text-purple-600" />
                  {t('useCases.request.weeklyAutoReminder')}
                </h3>
                <div className={`flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3 ${!isPending ? 'opacity-60' : ''}`}>
                  <div className="pr-4">
                    <div className="text-sm font-medium text-gray-900">{t('useCases.request.weeklyAutoReminder')}</div>
                    <div className="text-xs text-gray-500">{t('useCases.request.weeklyAutoReminderDescription')}</div>
                    {reminderCount > 0 && (
                      <div className="text-xs text-orange-600 mt-1">
                        {t('useCases.request.timelineEntries.reminderSent', { count: reminderCount })}
                        {lastReminder && ` · ${t('useCases.request.timelineEntries.reminderSentDetails', { date: new Date(lastReminder).toLocaleDateString() })}`}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={autoReminderOn}
                    disabled={isTogglingReminder || !isPending}
                    onClick={() => handleToggleAutoReminder(!autoReminderOn)}
                    className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors cursor-pointer disabled:cursor-not-allowed ${autoReminderOn ? 'bg-purple-600' : 'bg-gray-300'} ${isTogglingReminder ? 'opacity-70' : ''}`}
                  >
                    <span className={`pointer-events-none inline-flex h-4 w-4 items-center justify-center rounded-full bg-white shadow-sm transition-transform mt-0.5 ${autoReminderOn ? 'translate-x-4' : 'translate-x-0.5'}`}>
                      {isTogglingReminder && <Loader className="w-3 h-3 text-gray-400 animate-spin" />}
                    </span>
                  </button>
                </div>
                {isPending && (
                  <button
                    onClick={handleSendReminderNow}
                    disabled={isRemindingNow}
                    className="mt-3 w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-white border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRemindingNow ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    <span>{t('useCases.request.sendReminder')}</span>
                  </button>
                )}
              </div>
            )}

            {/* Actions */}
            {canApprove && (
              <div className="flex items-center space-x-4 pt-4 border-t border-gray-200">
                <button
                  onClick={handleApprove}
                  disabled={isSubmitting}
                  className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      {t('testimonials.requestDetail.approving')}
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      {t('testimonials.requestDetail.approveRequest')}
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  disabled={isSubmitting}
                  className="flex items-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="w-4 h-4 mr-2" />
                  {t('testimonials.requestDetail.rejectRequest')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('testimonials.requestDetail.rejectModalTitle')}</h3>
            <p className="text-sm text-gray-600 mb-4">{t('testimonials.requestDetail.rejectModalDescription')}</p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder={t('testimonials.requestDetail.rejectPlaceholder')}
            />
            <div className="flex items-center justify-end space-x-4 mt-6">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectionReason.trim() || isSubmitting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? t('testimonials.requestDetail.rejecting') : t('testimonials.requestDetail.reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialRequestDetail;
